// Location: src/features/devices/components/DeviceActions.tsx

import { useState } from 'react';
import {
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Alert,
  Divider,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  DeleteForever,
  VpnKey,
  Article,
} from '@mui/icons-material';
import { useDevicePermissions } from '../hooks/useDevicePermissions';
import { canViewErrorMonitor } from '@/utils/auth';
import OtpDialog from './OtpDialog';
import type { Device } from '@/types/models';
import type { ForceDeleteSummary } from '@/features/error-monitor/types';

interface DeviceActionsProps {
  record: Device;
}

/**
 * Row actions for Devices list
 * Shows menu with Edit and Delete (Deactivate) options based on permissions
 */
const DeviceActions = ({ record }: DeviceActionsProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const { canEdit, canDelete, canForceDelete, canGenerateOtp } = useDevicePermissions();
  const canViewLogs = canViewErrorMonitor();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [forceDeleteDialogOpen, setForceDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isForceDeleting, setIsForceDeleting] = useState(false);
  const [forceDeleteSummary, setForceDeleteSummary] = useState<ForceDeleteSummary | null>(null);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);

  const open = Boolean(anchorEl);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  // Action handlers
  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect('edit', 'devices', record.id);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      await dataProvider.delete('devices', {
        id: record.id,
        previousData: record,
      });

      notify('resources.devices.messages.deactivateInfo', { type: 'success' });
      handleDeleteDialogClose();
      refresh();
    } catch (error: any) {
      const errorMessage = error?.body?.error?.message ||
                          error?.message ||
                          translate('resources.devices.messages.deleteError');
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  // Force delete handlers
  const handleForceDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setForceDeleteDialogOpen(true);
    setForceDeleteSummary(null);
  };

  const handleForceDeleteDialogClose = () => {
    setForceDeleteDialogOpen(false);
    setForceDeleteSummary(null);
  };

  const handleForceDeleteConfirm = async () => {
    setIsForceDeleting(true);

    try {
      const result = await dataProvider.forceDeleteDevice(record.id);
      setForceDeleteSummary(result.data);
      notify('resources.devices.messages.forceDeleteSuccess', { type: 'success' });
      refresh();
    } catch (error: any) {
      const errorMessage = error?.body?.error?.message ||
                          error?.message ||
                          translate('resources.devices.messages.forceDeleteError');
      notify(errorMessage, { type: 'error' });
      handleForceDeleteDialogClose();
    } finally {
      setIsForceDeleting(false);
    }
  };

  const handleOtpClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    setOtpDialogOpen(true);
  };

  const handleViewLogs = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    redirect(`/logs?deviceId=${record.id}`);
  };

  // Check if device is already inactive
  const isInactive = record.status === 'Inactive';

  // Determine if any menu items would be visible
  const isDispenser = record.deviceType === 'Dispenser';
  const hasAnyAction = canEdit || canDelete || canForceDelete || (isDispenser && (canGenerateOtp || canViewLogs));

  // Don't render anything if no actions are available
  if (!hasAnyAction) return null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu')}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        id="device-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={(e: any) => handleClose(e)}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'device-actions-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Generate OTP - Dispenser only, Hotel administrator or above (P06) */}
        {canGenerateOtp && isDispenser && (
          <MenuItem onClick={handleOtpClick}>
            <ListItemIcon>
              <VpnKey fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.devices.actions.generateOtp')}</ListItemText>
          </MenuItem>
        )}

        {/* View Logs - Dispenser only, for whoever may open device logs (chain managers and technical users) */}
        {isDispenser && canViewLogs && (
          <MenuItem onClick={handleViewLogs}>
            <ListItemIcon>
              <Article fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.devices.actions.viewLogs')}</ListItemText>
          </MenuItem>
        )}

        {/* Edit */}
        {canEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}

        {/* Delete (Deactivate) */}
        {canDelete && (
          <MenuItem
            onClick={handleDeleteClick}
            disabled={isInactive}
            sx={{
              color: isInactive ? 'text.disabled' : 'error.main',
              '&:hover': {
                backgroundColor: isInactive ? 'transparent' : 'error.light',
              },
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color={isInactive ? 'disabled' : 'error'} />
            </ListItemIcon>
            <ListItemText>
              {translate('resources.devices.actions.deactivate')}
              {isInactive && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {translate('resources.devices.messages.alreadyInactive')}
                </Typography>
              )}
            </ListItemText>
          </MenuItem>
        )}

        {/* Force Delete - SuperAdmin only */}
        {canForceDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={handleForceDeleteClick}
              sx={{
                color: 'error.dark',
                '&:hover': {
                  backgroundColor: 'error.light',
                },
              }}
            >
              <ListItemIcon>
                <DeleteForever fontSize="small" sx={{ color: 'error.dark' }} />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" fontWeight={600}>
                  {translate('resources.devices.actions.forceDelete')}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {translate('resources.devices.messages.forceDeleteCaption')}
                </Typography>
              </ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete (Deactivate) Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {translate('resources.devices.messages.deactivateConfirm')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate('resources.devices.messages.deactivateWarning', {
              installationCode: record.installationCode,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting
              ? translate('ra.action.loading')
              : translate('resources.devices.actions.deactivate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Delete Confirmation Dialog */}
      <Dialog
        open={forceDeleteDialogOpen}
        onClose={handleForceDeleteDialogClose}
        onClick={(e) => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          {translate('resources.devices.messages.forceDeleteConfirm')}
        </DialogTitle>
        <DialogContent>
          {forceDeleteSummary ? (
            // Show deletion summary
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {translate('resources.devices.messages.forceDeleteComplete')}
              </Alert>
              <Typography variant="body2" gutterBottom>
                {translate('resources.devices.messages.forceDeleteSummary')}:
              </Typography>
              <Box sx={{ pl: 2, mt: 1 }}>
                <Typography variant="body2">
                  • {translate('resources.devices.messages.deletedJourneys')}: {forceDeleteSummary.deletedJourneys}
                </Typography>
                <Typography variant="body2">
                  • {translate('resources.devices.messages.deletedEvents')}: {forceDeleteSummary.deletedEvents}
                </Typography>
                {forceDeleteSummary.deletedDevices !== undefined && (
                  <Typography variant="body2">
                    • {translate('resources.devices.messages.deletedDevices')}: {forceDeleteSummary.deletedDevices}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            // Show warning before deletion
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {translate('resources.devices.messages.forceDeleteDanger')}
              </Alert>
              <DialogContentText>
                {translate('resources.devices.messages.forceDeleteWarning', {
                  installationCode: record.installationCode,
                })}
              </DialogContentText>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.devices.messages.forceDeleteWillRemove')}:
                </Typography>
                <Box sx={{ pl: 2, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    • {translate('resources.devices.messages.allJourneys')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • {translate('resources.devices.messages.allEvents')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • {translate('resources.devices.messages.theDevice')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {forceDeleteSummary ? (
            <Button onClick={handleForceDeleteDialogClose} variant="contained">
              {translate('ra.action.close')}
            </Button>
          ) : (
            <>
              <Button onClick={handleForceDeleteDialogClose} disabled={isForceDeleting}>
                {translate('ra.action.cancel')}
              </Button>
              <Button
                onClick={handleForceDeleteConfirm}
                color="error"
                variant="contained"
                disabled={isForceDeleting}
              >
                {isForceDeleting
                  ? translate('ra.action.loading')
                  : translate('resources.devices.actions.forceDeleteConfirm')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* OTP Dialog */}
      <OtpDialog
        open={otpDialogOpen}
        onClose={() => setOtpDialogOpen(false)}
        record={record}
      />
    </>
  );
};

export default DeviceActions;